import type { TaskStatus } from '@dyspatch/core';
import { BookOpen } from 'lucide-react';
import { TaskIcon, taskLook } from '@/components/common/StatusIcon';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Panel, PanelHead } from '@/components/ui/panel';
import { duration, waitThresholds } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * How it works, carried over from the server-rendered page.
 *
 * The copy is verbatim (REDESIGN-2 §2: "keep current content/copy verbatim,
 * restyled to the new tokens") because it is the one page in the console written
 * for somebody who has never seen the system, and rewording it in a port is how
 * an explanation quietly becomes wrong. Two sentences did change, and only
 * because the interface they describe changed: the role now shows in the left
 * rail rather than a top bar, and the gate colour is the accent rather than
 * purple specifically.
 *
 * The keyboard section is new. It is here rather than in a floating overlay
 * because `?` should take a person somewhere they can read at their own pace,
 * and because a shortcut nobody can find is a shortcut nobody has.
 */
const LIFECYCLE = [
  {
    title: 'A job gets opened',
    body: 'From Slack, a GitHub issue, the CLI, docs, the API, or a nightly schedule. Goal, repos, constraints.',
  },
  {
    title: 'A worker drafts the plan',
    body: 'Real repo code and acceptance criteria: issue-as-job, plan, agent peer review.',
  },
  {
    title: 'You approve the plan',
    body: 'Inline comments, then one click. Nothing runs until a person approves it.',
    gate: true,
  },
  {
    title: 'The scheduler hands it out',
    body: 'Offered to a worker; Claude runs headless in a throwaway git worktree.',
  },
  {
    title: 'The agent works',
    body: 'Lint, typecheck, tests pass before the branch pushes and a PR opens.',
  },
  {
    title: 'You review and merge',
    body: 'An ordinary PR on GitHub. The hub records who signed off.',
    gate: true,
  },
];

/**
 * Each card carries an id, because Fleet links straight at two of them: an
 * adapter that reports "off" is only actionable if the page can say what it
 * would do when it is on, and this is where that is written down.
 */
const SOURCES = [
  {
    glyph: 'sl',
    name: 'Slack',
    body: 'Mention the bot in #eng-requests with what you want and where.',
  },
  {
    glyph: 'gh',
    name: 'GitHub',
    body: 'Label any issue dyspatch, and it becomes the job verbatim.',
  },
  { glyph: '>_', name: 'CLI', body: 'dys file "fix the flaky auth test" -r nymbl-app' },
  {
    glyph: 'cr',
    name: 'Schedule',
    body: 'Nightly and weekly standing jobs: docs refresh, dependency sweeps.',
  },
  {
    glyph: '{}',
    name: 'API',
    body: 'Filed through the API with a bearer token, for other systems.',
  },
  { glyph: 'db', name: 'Docs', body: 'Doc-sourced jobs from the knowledge-base watcher.' },
  {
    glyph: 'hx',
    name: 'Helix',
    body: 'L3 stories with acceptance criteria are filed as dyspatch:helix GitHub issues on their mapped repo, then flow like any labelled issue.',
  },
];

/**
 * One row per word a task's state reads as, each carrying the `TaskStatus`
 * key (or two, when a row folds a handshake into one word) that draws its
 * icon and colour. This is the legend issue #65 asked for -- reachable once,
 * from `?` or the rail, rather than something a person has to memorise off
 * eight colours -- and it draws through `TaskIcon` itself, the same component
 * a row, a board card, a graph node and the plan list draw through, so the
 * legend cannot show a colour or a shape the rest of the console does not
 * actually use.
 */
interface StateRow {
  statuses: readonly [TaskStatus, ...TaskStatus[]];
  /** Only set when the row folds more than one status into one word. */
  word?: string;
  body: string;
}

const STATES: readonly StateRow[] = [
  { statuses: ['queued'], body: 'Approved and waiting for a free worker.' },
  {
    statuses: ['offered', 'claimed'],
    word: 'offered / claimed',
    body: 'A worker is being handed the task; falls back to queued on timeout.',
  },
  {
    statuses: ['running'],
    body: "An agent is working in a throwaway worktree on someone's machine.",
  },
  { statuses: ['reporting'], body: 'Work finished; pushing the branch and opening the PR.' },
  {
    // `TASK_LOOK.review.label` is already "waiting on you" (StatusIcon.tsx),
    // so this row leans on the fallback below rather than repeating it.
    statuses: ['review'],
    body: 'A human gate is open: plan approval or PR review. The accent colour always means you.',
  },
  { statuses: ['done'], body: 'Merged (or completed with no PR needed). Sign-off recorded.' },
  { statuses: ['failed'], body: 'Something broke; requeue or cancel from the task page.' },
  { statuses: ['cancelled'], body: 'Withdrawn by a person. Terminal.' },
];

const KEYS: [string[], string][] = [
  [['⌘', 'K'], 'Open the command palette: jump anywhere, or run a gate action from anywhere.'],
  [['g', 'd'], 'Dashboard. `g i` still works, from when it was called the Inbox.'],
  [['g', 'j'], 'Jobs'],
  [['g', 'a'], 'Agents'],
  [['g', 's'], 'The task board, under Jobs. `g t` still works, from when they were called tasks.'],
  [['g', 'r'], 'Review queue'],
  [['g', 'p'], 'People, on the admin section'],
  [['g', 'm'], 'Your account'],
  [['j'], 'Down a row on the dashboard, the task board or Jobs.'],
  [['k'], 'Up a row. The arrows do the same once you are in the list.'],
  [['Enter'], 'Open the row under the ring.'],
  [
    ['e'],
    'On the dashboard: the gate’s first verb, and its question. Go on a plan, Mark merged on a PR.',
  ],
  [['x'], 'On the dashboard: the second verb, and its question. Reject a plan, send a PR back.'],
  [['c'], 'Open a new job.'],
  [['d'], 'Switch between roomy and compact density.'],
  [['?'], 'This page.'],
];

function Keycap({ children }: { children: string }) {
  return <kbd className="dy-kbd">{children}</kbd>;
}

export function AboutPage() {
  return (
    <>
      <ViewHeader
        title="How Dyspatch works"
        icon={BookOpen}
        sub="the lifecycle, the intake, the states"
      />
      <Page className="max-w-[52rem]">
        <div className="dy-stack">
          <Panel>
            <div className="px-4 py-3.5">
              <p className="text-fg-2 text-base leading-relaxed">
                Work opened anywhere at Nymbl comes back out as a pull request a human signed off
                on. The agents run on our own laptops, under our own Claude subscriptions, so the
                hub only coordinates and keeps the record.
              </p>
            </div>
          </Panel>

          <Panel>
            <PanelHead title="The lifecycle" meta="two of these six are yours" />
            <ol className="divide-y">
              {LIFECYCLE.map((step, index) => (
                <li
                  key={step.title}
                  className={cn('flex items-start gap-3 px-4 py-2.5', step.gate && 'bg-gate-bg/40')}
                >
                  <span
                    className={cn(
                      'tnum mt-[1px] w-4 shrink-0 text-sm font-semibold',
                      step.gate ? 'text-gate' : 'text-fg-3',
                    )}
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{step.title}</p>
                    <p className="text-fg-3 mt-0.5 text-xs">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Panel>

          <Panel>
            <PanelHead title="Filing work" meta="seven ways in, one shape out" />
            <div className="grid divide-y sm:grid-cols-2 sm:divide-y-0">
              {SOURCES.map((source, index) => (
                <div
                  key={source.name}
                  id={`intake-${source.name.toLowerCase()}`}
                  className={cn(
                    'flex scroll-mt-16 items-start gap-2.5 px-4 py-2.5',
                    index % 2 === 1 && 'sm:border-l',
                    index >= 2 && 'sm:border-t',
                  )}
                >
                  <span className="bg-raised text-fg-3 mt-[1px] grid h-[18px] w-6 shrink-0 place-items-center rounded-sm font-mono text-2xs">
                    {source.glyph}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{source.name}</p>
                    <p className="text-fg-3 mt-0.5 text-xs">{source.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel>
            <PanelHead title="What the states mean" />
            <dl className="divide-y">
              {STATES.map((row) => {
                const look = taskLook(row.statuses[0]);
                const word = row.word ?? look.label;
                return (
                  <div
                    key={row.statuses.join('/')}
                    className="grid grid-cols-[9rem_minmax(0,1fr)] gap-3 px-4 py-2"
                  >
                    <dt className={cn('flex items-center gap-1.5 text-sm font-medium', look.tone)}>
                      <span className="flex items-center gap-0.5">
                        {row.statuses.map((status) => (
                          <TaskIcon key={status} status={status} />
                        ))}
                      </span>
                      {word}
                    </dt>
                    <dd className="text-fg-3 text-xs">{row.body}</dd>
                  </div>
                );
              })}
            </dl>
            {/* The one late-ness rule, written down. Three panels used to draw
                their own line between "waiting" and "late", and a person who
                learned the colour on one page mis-read it on the next. */}
            <p className="text-fg-3 border-t px-4 py-2 text-2xs">
              A wait turns amber after {duration(waitThresholds().late)} and red at{' '}
              {duration(waitThresholds().stale)}, which is the interval the hub itself calls stale;
              the same two numbers colour every age, every gate row and the review queue.
            </p>
            {/* Issue #11: a queued step with an unmet dependency used to read
                identically to one simply waiting its turn. The full rule is
                written down once, in the doc, rather than restated here. */}
            <p className="text-fg-3 border-t px-4 py-2 text-2xs">
              A queued task waiting on a dependency names it rather than reading as plain{' '}
              <span className="font-mono">queued</span>, and a task whose dependency is itself stuck
              reads as blocked instead of waiting. The full rule -{' '}
              <a
                href="https://github.com/BeNYMBL/Nymbl-Dyspatch/blob/master/docs/JOB-LIFECYCLE.md#210-dependency-order"
                target="_blank"
                rel="noreferrer"
                className="hover:text-gate underline"
              >
                dependency order, and what unblocks a task
              </a>
              .
            </p>
          </Panel>

          <Panel>
            <PanelHead title="Keyboard" meta="the console is meant to be driven from here" />
            <dl className="divide-y">
              {KEYS.map(([keys, meaning]) => (
                <div
                  key={keys.join('-')}
                  className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 px-4 py-2"
                >
                  <dt className="flex items-center gap-1">
                    {keys.map((key) => (
                      <Keycap key={key}>{key}</Keycap>
                    ))}
                  </dt>
                  <dd className="text-fg-3 text-xs">{meaning}</dd>
                </div>
              ))}
            </dl>
            <p className="text-fg-3 border-t px-4 py-2 text-2xs">
              A <Keycap>g</Keycap> sequence waits a second and a half for its second key, and the
              rail says <Keycap>g…</Keycap> while it does. Nothing fires while the cursor is in a
              field, while the palette is open, or while that sequence is waiting.
            </p>
          </Panel>

          <Panel>
            <PanelHead title="Members and admins" />
            <div className="space-y-2 px-4 py-3">
              <p className="text-fg-2 text-sm">
                Everyone files jobs, approves plans for their own work, and reviews PRs they're
                asked to. <b className="font-semibold">Admins</b> additionally manage workers and
                enrollment tokens, invite people, set per-person concurrency limits, and can act on
                any task. Your role shows under your name in the left rail.
              </p>
              <p className="text-fg-3 text-xs">
                Identity is an account, not a header. You sign in with an email and a password the
                hub holds a hash of, the session is a cookie this browser cannot read, and your role
                is a column on your user row that the server checks on every write. Admin is
                something an admin grants on People; the first person to sign up gets it, because
                somebody has to.
              </p>
            </div>
          </Panel>
        </div>
      </Page>
    </>
  );
}
